import { memo } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, FileText } from 'lucide-react';
import { useDocsStore } from '../../stores/docsStore';
import type { TreeNode } from '../../types/docs';

interface FileTreeNodeProps {
  node: TreeNode;
  depth: number;
}

function formatSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function FileTreeNodeInner({ node, depth }: FileTreeNodeProps) {
  const { selectedPath, expandDirectory, collapseDirectory, selectFile } = useDocsStore();
  const isSelected = selectedPath === node.path;

  const handleClick = () => {
    if (node.type === 'directory') {
      if (node.expanded) {
        collapseDirectory(node.path);
      } else {
        expandDirectory(node.path);
      }
    } else {
      selectFile(node.path);
    }
  };

  return (
    <div>
      <button
        onClick={handleClick}
        className={`w-full flex items-center gap-1.5 px-2 py-1 text-left text-xs hover:bg-bg-tertiary rounded transition-colors ${
          isSelected ? 'bg-accent-purple/15 text-accent-purple-light' : 'text-text-secondary'
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        {node.type === 'directory' ? (
          <>
            {node.expanded ? (
              <ChevronDown className="h-3 w-3 flex-shrink-0 text-text-muted" />
            ) : (
              <ChevronRight className="h-3 w-3 flex-shrink-0 text-text-muted" />
            )}
            {node.expanded ? (
              <FolderOpen className="h-3.5 w-3.5 flex-shrink-0 text-warning" />
            ) : (
              <Folder className="h-3.5 w-3.5 flex-shrink-0 text-warning" />
            )}
          </>
        ) : (
          <>
            <span className="w-3 flex-shrink-0" />
            <FileText className="h-3.5 w-3.5 flex-shrink-0 text-text-muted" />
          </>
        )}
        <span className="truncate flex-1">{node.name}</span>
        {node.type === 'file' && node.size !== undefined && (
          <span className="text-text-muted text-[10px] flex-shrink-0">{formatSize(node.size)}</span>
        )}
      </button>

      {/* Render children if expanded */}
      {node.type === 'directory' && node.expanded && node.children && (
        <div>
          {node.children.map(child => (
            <FileTreeNodeMemo key={child.path} node={child} depth={depth + 1} />
          ))}
        </div>
      )}

      {/* Loading indicator for expanding directories */}
      {node.type === 'directory' && node.expanded && !node.loaded && (
        <div className="flex items-center gap-2 py-1 text-text-muted text-[10px]" style={{ paddingLeft: `${24 + depth * 16}px` }}>
          <div className="h-3 w-3 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
          Loading...
        </div>
      )}
    </div>
  );
}

const FileTreeNodeMemo = memo(FileTreeNodeInner);

export { FileTreeNodeMemo as FileTreeNode };
export default FileTreeNodeMemo;
